import { ChangeDetectionStrategy, Component, Input, computed, inject, signal } from '@angular/core';
import { DataSourceDirective } from 'eurogard-data-source/directive';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SubscribedRoutersPanelDataSource } from './subscribed-routers-panel.data-source';
import {
  PriorityItem,
  PrioritySortingControlComponent,
} from 'angular-controls/priority-sorting-control/priority-sorting-control.component';
import { AutocompleteItem } from 'angular-forms/autocomplete-form/types';
import { AUTOCOMPLETE_ITEM_AUTOCOMPLETE_OPTIONS_METHOD } from 'angular-forms/autocomplete-form/tokens';
import { SubscribedRoutersAutocompleteMethod } from './subscribed-routers-autocomplete.method';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  UserSettingsAccordionDataSource
} from 'angular-components/layout/default-header/user-profile-icon/user-settings/user-settings-accordion.data-source';
import { NgIf } from '@angular/common';
import { DataSourceErrorComponent } from 'eurogard-data-source';
import {
  NotificationAccordionSubscribedRoutersControllerUpdateSubscriptionsRemoteMethod
} from 'open-api-service-shared-features/remote-methods/notification-accordion-subscribed-routers-controller-update-subscriptions.remote-method';

interface AutocompleteMethod extends AutocompleteItem {
  name: string;
}

@Component({
  standalone: true,
  selector: 'eurogard-subscribed-routers-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './subscribed-routers-panel.component.html',
  styleUrls: [ './subscribed-routers-panel.component.scss' ],
  imports: [
    DataSourceDirective,
    MatProgressBarModule,
    PrioritySortingControlComponent,
    FormsModule,
    NgIf,
    DataSourceErrorComponent,
  ],
  providers: [
    SubscribedRoutersPanelDataSource,
    UserSettingsAccordionDataSource,
    {
      provide: AUTOCOMPLETE_ITEM_AUTOCOMPLETE_OPTIONS_METHOD,
      useClass: SubscribedRoutersAutocompleteMethod,
    },
  ],
})
export class SubscribedRoutersPanelComponent {
  @Input() userUuid?: string;

  public readonly panelDataSource    = inject(SubscribedRoutersPanelDataSource);
  public readonly updating           = signal(false);
  public readonly loading            = computed(
    () => this.updating() || this.panelDataSource.loading(),
  );
  public nextPriority: number | null = null;

  public readonly data                       = toSignal(
    this.panelDataSource.connect({id: 'to-signal-subscribed-routers-panel'}),
  );
  private readonly updateSubscriptionsMethod = inject(
    NotificationAccordionSubscribedRoutersControllerUpdateSubscriptionsRemoteMethod,
  );

  protected readonly autocompleteLabel = $localize`Router subscriber`;
  protected readonly autoCompletePlaceholder = $localize`Add a new router subscriber to this priority group`;

  get currentUserUuid() {
    return this.userUuid ?? this.panelDataSource.data?.uuid;
  }

  async updateSubscriber(routerList: Array<PriorityItem<AutocompleteMethod>>) {
    this.updating.set(true);
    await this.updateSubscriptionsMethod.call({
      parameters: {
        uuid: this.currentUserUuid!,
      },
      requestBody: {
        uuid: this.currentUserUuid!,
        routerList: routerList,
      },
    });
    this.panelDataSource.refresh();
    this.updating.set(false);
  }
}
